"use client"

import React from "react"
import { NotificationItemTemplate } from "./NotificationItemTemplate"
import { FragmentType, graphql, useFragment } from "@/gql"
import { ReferralRequestStatus } from "@/gql/graphql"
import { NotificationActor } from "./NotificationActor"
import { useMutation } from "@apollo/client"
import { Button, Text } from "@/elements"
import { ampli } from "@/ampli"

const ReceivedReferralRequestFragment = graphql(`
  fragment ReceivedReferralRequest on StreamNotificationFeedAggregatedActivity {
    id
    isSeen
    activities {
      id
      time
      target {
        ... on JobPosting {
          id
          title
        }
      }
      foreignObject {
        __typename
        ... on ReferralRequest {
          id
          status
        }
      }
      actor {
        id
        name
        ...NotificationActor
      }
    }
  }
`)

const confirmReferralRequestMutation = graphql(`
  mutation ConfirmReferralRequest($referralRequestId: ID!) {
    confirmReferralRequest(referralRequestId: $referralRequestId) {
      ok
    }
  }
`)

const ignoreReferralRequestMutation = graphql(`
  mutation IgnoreReferralRequest($referralRequestId: ID!) {
    ignoreReferralRequest(referralRequestId: $referralRequestId) {
      ok
    }
  }
`)

interface Props {
  onActorClick: (e: React.MouseEvent<any, MouseEvent>) => void
  item: FragmentType<typeof ReceivedReferralRequestFragment>
}

export const ReceivedReferralRequest: React.FC<Props> = props => {
  const item = useFragment(ReceivedReferralRequestFragment, props.item)
  const activities = item.activities?.[0]
  const referralRequest = activities?.foreignObject
  const referralRequestId = referralRequest?.__typename === "ReferralRequest" ? referralRequest?.id : ""

  const actor = activities?.actor
  const actorName = actor?.name

  const [confirm, { called: confirmCalled, reset: resetConfirm }] = useMutation(confirmReferralRequestMutation, {
    variables: {
      referralRequestId: referralRequestId!,
    },
    refetchQueries: "active",
    awaitRefetchQueries: true,
    onCompleted: () => {
      ampli.referralRequestAccept({
        entry_point: "notifications",
      })
    },
    onError: () => {
      resetConfirm()
    },
  })
  const [ignore, { called: ignoreCalled, reset: resetIgnore }] = useMutation(ignoreReferralRequestMutation, {
    variables: {
      referralRequestId: referralRequestId!,
    },
    onCompleted: () => {
      ampli.referralRequestIgnore({
        entry_point: "notifications",
      })
    },
    refetchQueries: "active",
    awaitRefetchQueries: true,
    onError: () => {
      resetIgnore()
    },
  })

  const handleConfirm = () => {
    confirm()
  }
  const handleIgnore = () => {
    ignore()
  }

  return (
    <NotificationItemTemplate
      isSeen={item.isSeen}
      LeftComponent={actor && <NotificationActor actor={actor} onActorClick={props.onActorClick} />}
      TextContent={
        <Text element="span">
          <Text element="span" className="!font-medium">
            {actorName}
          </Text>{" "}
          has requested a referral to join NOVA.
        </Text>
      }
      time={activities?.time}
      ActionsBar={
        referralRequestId &&
        referralRequest?.__typename === "ReferralRequest" &&
        referralRequest.status === ReferralRequestStatus.Pending ? (
          <div className="mt-2 flex flex-row gap-2">
            <Button size="small" onClick={handleConfirm} disabled={confirmCalled || ignoreCalled}>
              Confirm
            </Button>
            <Button size="small" variant="secondary" onClick={handleIgnore} disabled={confirmCalled || ignoreCalled}>
              Ignore
            </Button>
          </div>
        ) : null
      }
    />
  )
}
